/**
 * useOutcomesReportExport — initiate an Outcomes-report PDF export and poll it
 * to completion.
 *
 * Rides the shared async export pipeline (the same one the Cause report uses):
 *   POST /v1/datasets/{id}/outcomes-report/export        → { export_id }
 *   GET  /v1/datasets/{id}/outcomes-report/export/{id}/status → status/progress
 *   GET  .../export/{id}/download                        → the PDF (authed)
 *
 * A filter/grain change is not relevant here — an export is a point-in-time
 * snapshot of the current framework + filters. Kept self-contained so removing
 * the Cause report UI can't break it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { fetchWithAuth } from '../../../../../utils/api';
import { mapFiltersToApiParams } from '../../cause-report/utils';
import type { FilterConfig } from '../../../records/filter';
import type { Framework } from '../types';

export type ExportStatus = 'idle' | 'generating' | 'ready' | 'error';

export interface OutcomesExportRequest {
  datasetId: string;
  framework: Framework;
  filters: FilterConfig;
  metadata?: Record<string, string>;
}

interface ExportApiResponse {
  export_id: string;
  status: string;
  progress?: number;
  download_url?: string;
  expires_at?: string;
  error?: string;
}

async function initiateExport(request: OutcomesExportRequest): Promise<ExportApiResponse> {
  return fetchWithAuth(`/v1/datasets/${request.datasetId}/outcomes-report/export`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      format: 'pdf',
      framework: request.framework,
      filters: mapFiltersToApiParams(request.filters),
      include_sections: { overview: true },
      metadata: request.metadata,
    }),
  });
}

async function checkExportStatus(datasetId: string, exportId: string): Promise<ExportApiResponse> {
  return fetchWithAuth(`/v1/datasets/${datasetId}/outcomes-report/export/${exportId}/status`);
}

/** The caller's current resumable export for this dataset (in-flight or ready),
 *  or null when there is none — the server-side source of truth we rehydrate
 *  from on mount, so an export survives a page unmount/refresh without stashing
 *  anything client-side (the backend scopes it to this user + dataset). Returns
 *  null on the 204 the endpoint sends when nothing is resumable. */
async function checkCurrentExport(
  datasetId: string,
  framework: string,
): Promise<ExportApiResponse | null> {
  const resp = await fetchWithAuth(
    `/v1/datasets/${datasetId}/outcomes-report/export/current`
    + `?framework=${encodeURIComponent(framework)}`,
  );
  return resp && (resp as ExportApiResponse).export_id ? (resp as ExportApiResponse) : null;
}

/** Total time to keep polling before giving up. Matched to the backend's
 *  export stale threshold (CAUSE_REPORT_EXPORT_STALE_MINUTES = 5 min) so the UI
 *  doesn't error out while the worker is still legitimately generating. */
const EXPORT_TIMEOUT = 300000; // 5 min
/** Deadline for the rehydrate `/export/current` lookup. fetchWithAuth has no
 *  request timeout, so without this a stalled recovery would pin isRecovering
 *  (and the disabled button) on indefinitely with no way to retry. */
const RECOVERY_TIMEOUT = 15000; // 15 s
/** Short first delay so early progress shows quickly. */
const INITIAL_POLL_DELAY = 500;
/** Cap for the exponential backoff. */
const MAX_POLL_INTERVAL = 10000;
const BACKOFF_MULTIPLIER = 1.5;
/** Consecutive transient poll failures tolerated before surfacing an error. */
const MAX_POLL_FAILURES = 3;

export interface UseOutcomesReportExportReturn {
  exportReport: (request: OutcomesExportRequest) => void;
  status: ExportStatus;
  progress: number;
  downloadUrl?: string;
  error?: string;
  reset: () => void;
  isExporting: boolean;
  /** True when the current state was rehydrated from a server-side in-flight
   *  export on mount (not a fresh click), so the UI can open the dialog for it. */
  resumed: boolean;
}

export function useOutcomesReportExport(
  datasetId?: string,
  framework?: string,
): UseOutcomesReportExportReturn {
  const [status, setStatus] = useState<ExportStatus>('idle');
  const [progress, setProgress] = useState<number>(0);
  const [downloadUrl, setDownloadUrl] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [resumed, setResumed] = useState<boolean>(false);
  // True while the on-mount /export/current check is in flight, so the Export
  // button stays disabled and a click can't race (and 429) the existing job.
  const [isRecovering, setIsRecovering] = useState<boolean>(false);

  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentIntervalRef = useRef<number>(INITIAL_POLL_DELAY);
  const pollFailuresRef = useRef<number>(0);
  // Bumped on every new export and on reset. An in-flight fetch/poll promise
  // captures the generation it started under; if it resolves after a newer
  // export or a reset, its generation is stale and it must not touch state or
  // schedule another poll.
  const genRef = useRef<number>(0);

  const cleanup = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    currentIntervalRef.current = INITIAL_POLL_DELAY;
  }, []);

  // On unmount, invalidate the current generation before clearing timers so an
  // already-in-flight poll response can't call setState after unmount.
  useEffect(() => () => {
    genRef.current += 1;
    cleanup();
  }, [cleanup]);

  // Arm the overall deadline. Called from onMutate so it covers the WHOLE flow —
  // initiation AND polling: fetchWithAuth has no request timeout and
  // OutcomesExportButton blocks every close while 'generating', so a stalled
  // POST would otherwise leave the dialog stuck indefinitely. A response that
  // lands after the deadline is invalidated by the generation bump.
  const armTimeout = useCallback((gen: number) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (gen !== genRef.current) return;
      // Invalidate this generation so a response arriving just after the timeout
      // can't undo the error state or reschedule polling.
      genRef.current += 1;
      cleanup();
      setStatus('error');
      setError('Export timed out. Please try again.');
    }, EXPORT_TIMEOUT);
  }, [cleanup]);

  const startPolling = useCallback((datasetId: string, exportId: string, gen: number) => {
    currentIntervalRef.current = INITIAL_POLL_DELAY;
    pollFailuresRef.current = 0;
    // The deadline is already armed in onMutate (covering initiation); do not
    // re-arm here or it would extend past initiation by another EXPORT_TIMEOUT.

    const poll = async () => {
      try {
        const response = await checkExportStatus(datasetId, exportId);
        // A newer export or a reset happened while this request was in flight —
        // drop the stale result rather than overwrite the current state.
        if (gen !== genRef.current) return;

        if (response.status === 'ready') {
          cleanup();
          setProgress(100);
          setStatus('ready');
          setDownloadUrl(response.download_url);
          return;
        }
        if (response.status === 'error') {
          cleanup();
          setStatus('error');
          setError(response.error || 'Export failed');
          return;
        }

        pollFailuresRef.current = 0; // a good response clears the transient count
        setProgress(response.progress ?? 0);
        currentIntervalRef.current = Math.min(
          currentIntervalRef.current * BACKOFF_MULTIPLIER,
          MAX_POLL_INTERVAL,
        );
        pollTimeoutRef.current = setTimeout(poll, currentIntervalRef.current);
      } catch {
        if (gen !== genRef.current) return;
        // A single status request can fail transiently (brief network blip) while
        // the backend job is still running — retry a few times before giving up
        // rather than erroring out and orphaning a live export.
        pollFailuresRef.current += 1;
        if (pollFailuresRef.current <= MAX_POLL_FAILURES) {
          pollTimeoutRef.current = setTimeout(poll, currentIntervalRef.current);
          return;
        }
        cleanup();
        setStatus('error');
        setError('Failed to check export status');
      }
    };

    pollTimeoutRef.current = setTimeout(poll, currentIntervalRef.current);
  }, [cleanup]);

  const mutation = useMutation({
    mutationFn: (request: OutcomesExportRequest) => initiateExport(request),
    // A new export opens a new generation; the returned gen is checked in the
    // success/error handlers so a superseded initiation can't write state.
    onMutate: () => {
      const gen = (genRef.current += 1);
      setStatus('generating');
      setProgress(0);
      setDownloadUrl(undefined);
      setError(undefined);
      setResumed(false); // a user-initiated export, not a rehydrated one
      // Start the deadline at initiation so a stalled POST times out too, not
      // just a stalled poll loop.
      armTimeout(gen);
      return { gen };
    },
    onSuccess: (response, request, context) => {
      if (!context || context.gen !== genRef.current) return;
      if (response.status === 'ready') {
        // Immediate terminal response — clear the deadline armed in onMutate, or
        // it fires EXPORT_TIMEOUT later and overwrites this state with an error.
        cleanup();
        setStatus('ready');
        setDownloadUrl(response.download_url);
      } else if (response.status === 'error') {
        cleanup();
        setStatus('error');
        setError(response.error);
      } else {
        setProgress(response.progress ?? 0);
        startPolling(request.datasetId, response.export_id, context.gen);
      }
    },
    onError: (err: Error, _request, context) => {
      if (context && context.gen !== genRef.current) return;
      cleanup();
      setStatus('error');
      setError(err.message || 'Failed to initiate export');
    },
  });

  const reset = useCallback(() => {
    // Invalidate any in-flight initiation/poll so it can't restore state.
    genRef.current += 1;
    cleanup();
    setStatus('idle');
    setProgress(0);
    setDownloadUrl(undefined);
    setError(undefined);
    setResumed(false);
    setIsRecovering(false);
  }, [cleanup]);

  // Rehydrate an export the server still considers live for this user + dataset +
  // framework (in-flight or ready) after a page unmount / refresh / back-nav, so
  // the user can watch it finish and download it instead of orphaning it and
  // hitting 429. Re-runs when the dataset or framework changes; the cleanup
  // invalidates this run's generation so React StrictMode's replay (and real
  // remounts) restart cleanly. The button is disabled while recovering, so a
  // click can't race the check; a click mid-check would bump genRef and this
  // bails rather than clobbering the fresh export.
  useEffect(() => {
    if (!datasetId || !framework) return;
    const gen = (genRef.current += 1);
    // Switching dataset/framework: drop the previous one's state before
    // recovering, so a target with no current export can't leave a stale
    // 'generating'/'ready' stuck in the dialog.
    cleanup();
    setStatus('idle');
    setProgress(0);
    setDownloadUrl(undefined);
    setError(undefined);
    setResumed(false);
    setIsRecovering(true);
    void (async () => {
      let recoveryTimer: ReturnType<typeof setTimeout> | undefined;
      try {
        // Bound the lookup: a stalled /export/current must not leave the button
        // disabled forever. On timeout the race rejects → catch → finally clears
        // isRecovering; the real response, if it lands later, is discarded here
        // (we've moved past the await) and by the generation guard.
        const current = await Promise.race([
          checkCurrentExport(datasetId, framework),
          new Promise<never>((_, reject) => {
            recoveryTimer = setTimeout(
              () => reject(new Error('recovery timed out')),
              RECOVERY_TIMEOUT,
            );
          }),
        ]);
        if (gen !== genRef.current) return;
        if (!current) return; // nothing resumable → stay idle
        if (current.status === 'ready') {
          setResumed(true);
          setStatus('ready');
          setDownloadUrl(current.download_url);
        } else {
          setResumed(true);
          setStatus('generating');
          setProgress(current.progress ?? 0);
          armTimeout(gen);
          startPolling(datasetId, current.export_id, gen);
        }
      } catch {
        // Transient failure — the server stays the source of truth, so a later
        // mount simply retries; nothing to clean up here (no client-side stash).
        if (gen !== genRef.current) return;
      } finally {
        if (recoveryTimer) clearTimeout(recoveryTimer);
        if (gen === genRef.current) setIsRecovering(false);
      }
    })();
    return () => {
      genRef.current += 1;
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetId, framework]);

  return {
    exportReport: mutation.mutate,
    status,
    progress,
    downloadUrl,
    error,
    reset,
    // Disabled during recovery too, so a click can't race the /current check.
    isExporting: status === 'generating' || isRecovering,
    resumed,
  };
}
